import React, { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogActions,
  DialogContent, DialogTitle
} from '@mui/material';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import { confirmButton, cancelButton, pageActionsContainer } from '../../components/Buttons';
import TagsInput from '../../components/TagsInput';
import InputWithIcon from '../../components/InputWithIcon';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import testTubeIcon from '../../assets/protocols/test-tube.svg';
import laboratoryIcon from '../../assets/protocols/laboratory.svg';
import sampleSourceIcon from '../../assets/protocols/sample-source.svg';
import collectionToolIcon from '../../assets/protocols/collection-tool.svg';
import colorPaletteIcon from '../../assets/protocols/color-palette.svg';
import instructionsIcon from '../../assets/protocols/instructions.svg';
import notesIcon from '../../assets/protocols/notes.svg';
import tagsIcon from '../../assets/protocols/tags.svg';
import '../../components/InputWithIcon.scss';

// Color mapping for Hebrew color names
const colorMap = {
  'אדום': 'red',
  'כחול': 'blue',
  'ירוק': 'green',
  'צהוב': 'yellow',
  'סגול': 'purple',
  'שחור': 'black',
  'כתום': 'orange',
  'ורוד': 'pink',
  'אפור': 'gray',
  'לבן': 'white',
  'חום': 'brown',
  'טורקיז': 'turquoise'
};

// Detect color in a string
const detectColor = (text: string): string | null => {
  if (!text) return null;
  
  for (const [hebrewColor, englishValue] of Object.entries(colorMap)) {
    if (text.includes(hebrewColor)) {
      return englishValue;
    }
  }
  return null;
};

interface TestTubeData {
  mainTitle: string;
  shortName: string;
  performingLaboratory: string;
  sampleSource: string;
  collectionTool: string;
  color: string;
  collectionInstructions: string;
  clarificationsAndExplanations: string;
  notes: string;
  keywords: string[];
  hiddenKeywords: string[];
}

interface AddEditTestTubeProps {
  open: boolean;
  onClose: () => void;
  testTube: TestTubeData;
  editMode: boolean;
  onSave: (testTube: TestTubeData) => void;
  isSubmitting: boolean;
}

const AddEditTestTube: React.FC<AddEditTestTubeProps> = ({
  open,
  onClose,
  testTube,
  editMode,
  onSave,
  isSubmitting
}) => {
  const [currentTestTube, setCurrentTestTube] = useState<TestTubeData>(testTube);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (testTube) {
      setCurrentTestTube(testTube);
    }
  }, [testTube]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentTestTube({ ...currentTestTube, [name]: value });
    
    // Clear validation error if the required field is filled
    if (name === 'mainTitle' && value.trim() && validationError) {
      setValidationError(null);
    }
  };

  const handleCollectionToolBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const detectedColor = detectColor(value);
    if (detectedColor && !currentTestTube.color) {
      setCurrentTestTube({ ...currentTestTube, color: detectedColor });
    }
  };

  const handleKeywordsChange = (newKeywords: string[]) => {
    setCurrentTestTube({
      ...currentTestTube,
      keywords: newKeywords
    });
  };

  const handleHiddenKeywordsChange = (newHiddenKeywords: string[]) => {
    setCurrentTestTube({
      ...currentTestTube,
      hiddenKeywords: newHiddenKeywords
    });
  };

  const handleSave = () => {
    // Validate required field
    if (!currentTestTube.mainTitle.trim()) {
      setValidationError('כותרת ראשית הינה שדה חובה');
      return;
    }

    onSave(currentTestTube);
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentTestTube({ ...currentTestTube, color: e.target.value });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir="rtl"
      sx={{
        '& .input-field': {
          marginBottom: '16px'
        },
        '& .error-text': {
          color: '#d32f2f',
          fontSize: '0.75rem',
          marginTop: '3px',
          marginLeft: '14px'
        }
      }}
    >
      <DialogTitle>
        {editMode ? 'עריכת מבחנה' : 'הוספת מבחנה חדשה'}
      </DialogTitle>
      <DialogContent style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div className="input-field">
          <InputWithIcon
            icon={testTubeIcon}
            placeholder="כותרת ראשית *"
            name="mainTitle"
            value={currentTestTube.mainTitle}
            onChange={handleInputChange}
            error={!!validationError}
            required
          />
          {validationError && <div className="error-text">{validationError}</div>}
        </div>

        <div className="input-field">
          <InputWithIcon
            icon={testTubeIcon}
            placeholder="שם מקוצר"
            name="shortName"
            value={currentTestTube.shortName}
            onChange={handleInputChange}
          />
        </div>

        <div className="input-field">
          <InputWithIcon
            icon={laboratoryIcon}
            placeholder="מעבדה מבצעת"
            name="performingLaboratory"
            value={currentTestTube.performingLaboratory}
            onChange={handleInputChange}
          />
        </div>

        <div className="input-field">
          <InputWithIcon
            icon={sampleSourceIcon}
            placeholder="מקור דגימה"
            name="sampleSource"
            value={currentTestTube.sampleSource}
            onChange={handleInputChange}
          />
        </div>

        <div className="input-field">
          <InputWithIcon
            icon={collectionToolIcon}
            placeholder="כלי איסוף"
            name="collectionTool"
            value={currentTestTube.collectionTool}
            onChange={handleInputChange}
            onBlur={handleCollectionToolBlur}
          />
        </div>

        <DropdownWithIcon
          icon={colorPaletteIcon}
          placeholder="בחר צבע"
          value={currentTestTube.color}
          onChange={handleColorChange}
        >
          {Object.entries(colorMap).map(([hebrewName, englishValue]) => {
            let colorCircle = '●'; // Default black circle
            switch(englishValue) {
              case 'red': colorCircle = '🔴'; break;
              case 'blue': colorCircle = '🔵'; break;
              case 'green': colorCircle = '🟢'; break;
              case 'yellow': colorCircle = '🟡'; break;
              case 'purple': colorCircle = '🟣'; break;
              case 'black': colorCircle = '⚫'; break;
              case 'orange': colorCircle = '🟠'; break;
              case 'white': colorCircle = '⚪'; break;
              case 'brown': colorCircle = '🟤'; break;
              case 'pink': colorCircle = '🔴'; break;
              case 'gray': colorCircle = '⚫'; break; // Using black for gray as there's no gray emoji
              case 'turquoise': colorCircle = '🔵'; break; // Using blue for turquoise
              default: colorCircle = '●';
            }
            return (
              <option key={englishValue} value={englishValue}>
                {colorCircle} {hebrewName}
              </option>
            );
          })}
        </DropdownWithIcon>

        <div className="input-field">
          <InputWithIcon
            icon={instructionsIcon}
            placeholder="הנחיות לקיחה"
            name="collectionInstructions"
            value={currentTestTube.collectionInstructions}
            onChange={handleInputChange}
            multiline
            rows={2}
          />
        </div>

        <div className="input-field">
          <InputWithIcon
            icon={instructionsIcon}
            placeholder="הבהרות והסברים"
            name="clarificationsAndExplanations"
            value={currentTestTube.clarificationsAndExplanations}
            onChange={handleInputChange}
            multiline
            rows={3}
          />
        </div>

        <div className="input-field">
          <InputWithIcon
            icon={notesIcon}
            placeholder="הערות"
            name="notes"
            value={currentTestTube.notes}
            onChange={handleInputChange}
            multiline
            rows={2}
          />
        </div>
        
        {/* Keywords Section */}
        <div style={{ marginTop: '8px' }}>
          <TagsInput
            tags={currentTestTube.keywords}
            onChange={handleKeywordsChange}
            label="מילות מפתח"
            placeholder="הקלד מילת מפתח ולחץ אנטר להוספה"
            icon={tagsIcon}
          />
        </div>

        {/* Hidden Keywords Section */}
        <div style={{ marginTop: '8px' }}>
          <TagsInput
            tags={currentTestTube.hiddenKeywords}
            onChange={handleHiddenKeywordsChange}
            label="מילות מפתח נסתרות"
            placeholder="הקלד מילת מפתח נסתרת ולחץ אנטר להוספה"
            icon={tagsIcon}
          />
        </div>
      </DialogContent>
      <DialogActions style={pageActionsContainer}>
        <button
          type="button"
          style={cancelButton}
          onClick={onClose}
          disabled={isSubmitting}
        >
          ביטול
        </button>
        <LoadingSubmitButton
          isLoading={isSubmitting}
          text={editMode ? "עדכן" : "שמור"}
          loadingText={editMode ? "מעדכן..." : "שומר..."}
          type="submit"
          onClick={handleSave}
          style={confirmButton}
          disabled={isSubmitting}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AddEditTestTube;